import { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Clock, Coffee, LogIn, LogOut, Delete, Loader2, Check, X, Camera, CameraOff, ChevronRight, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

/**
 * Staff clock-in/out tile + PIN modal for the kiosk.
 *
 * - `variant="tile"` renders a full button (use on KioskHome).
 * - `variant="chip"` renders a compact pill (use on SchoolKioskHome where tiles are denser).
 */
export default function StaffClockTile({ variant = "tile", wide = true }) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const [pin, setPin] = useState("");
  const [stage, setStage] = useState("pin"); // pin | enroll | actions | done
  const [enrollPhoto, setEnrollPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [identified, setIdentified] = useState(null); // {user_id, name, role, shift}
  const [lastDone, setLastDone] = useState(null); // {action, name}
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);

  // Open the camera once we know who the staff member is — keep it private to the action stage.
  const startCamera = useCallback(async () => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
      setCameraDenied(true);
      return;
    }
    if (streamRef.current) {
      if (videoRef.current && videoRef.current.srcObject !== streamRef.current) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(() => {});
      }
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 480 }, height: { ideal: 360 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setCameraReady(true);
      setCameraDenied(false);
    } catch {
      setCameraReady(false);
      setCameraDenied(true);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  // Grab a single JPEG frame from the live video. Returns null if camera is not ready.
  const captureFrame = useCallback(() => {
    const v = videoRef.current;
    if (!v || !streamRef.current || v.videoWidth === 0) return null;
    const w = 320;
    const h = Math.round((v.videoHeight / v.videoWidth) * w);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(v, 0, 0, w, h);
    try {
      return canvas.toDataURL("image/jpeg", 0.65);
    } catch {
      return null;
    }
  }, []);

  const latestPin = useRef("");
  const checking = useRef(false);

  const reset = useCallback(() => {
    setPin("");
    latestPin.current = "";
    setStage("pin");
    setIdentified(null);
    setBusy(false);
    setLastDone(null);
    setCameraDenied(false);
    stopCamera();
  }, [stopCamera]);

  useEffect(() => { if (!open) reset(); }, [open, reset]);

  // Start the camera once the dialog reaches the actions stage. Stop it on close/done.
  useEffect(() => {
    if (open && (stage === "actions" || stage === "enroll")) {
      startCamera();
    } else if (stage !== "actions" && stage !== "enroll") {
      stopCamera();
    }
  }, [open, stage, startCamera, stopCamera]);

  // Re-attach the live stream whenever the <video> element is (re)mounted (enroll preview ↔ camera, enroll → actions).
  useEffect(() => {
    const v = videoRef.current;
    if (v && streamRef.current && v.srcObject !== streamRef.current) {
      v.srcObject = streamRef.current;
      v.play().catch(() => {});
    }
  }, [stage, enrollPhoto, cameraReady]);

  // Auto-close after success
  useEffect(() => {
    if (stage !== "done") return;
    const t = setTimeout(close, 2400);
    return () => clearTimeout(t);
  }, [stage]);

  const identify = async (typed, { silent = false } = {}) => {
    if (busy || (silent && checking.current)) return;
    if (silent) checking.current = true; else setBusy(true);
    try {
      const { data } = await api.post("/timesheet/identify", { pin: typed });
      if (silent && latestPin.current !== typed) return; // user kept typing — ignore stale match
      setIdentified(data);
      setEnrollPhoto(null);
      setStage(data.face_enrolled ? "actions" : "enroll");
    } catch (err) {
      // Silent auto-check (4+ digits): keep what was typed so longer PINs can continue.
      if (!silent) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || "PIN not recognized");
        setPin("");
        latestPin.current = "";
      }
    } finally {
      if (silent) {
        checking.current = false;
        if (latestPin.current !== typed && latestPin.current.length >= 4) identify(latestPin.current, { silent: true });
      } else setBusy(false);
    }
  };

  const enrollFace = async () => {
    if (!identified || busy || !enrollPhoto) return;
    setBusy(true);
    try {
      await api.post("/timesheet/enroll-face", { user_id: identified.user_id, pin, photo_data_url: enrollPhoto });
      toast.success(`Face ID set up for ${identified.name}`);
      setIdentified({ ...identified, face_enrolled: true, face_required: true });
      setStage("actions");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your photo — try again");
      setEnrollPhoto(null);
    } finally {
      setBusy(false);
    }
  };

  const submitClock = async (action) => {
    if (!identified || busy) return;
    setBusy(true);
    try {
      const photo = captureFrame();
      const { data } = await api.post("/timesheet/clock", {
        user_id: identified.user_id,
        pin,
        action,
        ...(photo ? { photo_data_url: photo } : {}),
      });
      const label = {
        in: "Clocked in",
        out: "Clocked out",
        break_start: "Break started",
        break_end: "Break ended",
      }[action];
      setLastDone({ action, name: identified.name, label, shift: data, photo });
      setStage("done");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const lastTouch = useRef(0);
  const tap = (fn) => ({
    onPointerDown: (e) => { if (e.pointerType === "touch") { lastTouch.current = Date.now(); fn(); } },
    onClick: () => { if (Date.now() - lastTouch.current > 600) fn(); },
  });

  const pressDigit = (d) => {
    if (pin.length >= 10) return;
    const next = pin + d;
    setPin(next);
    latestPin.current = next;
    // Quietly try the PIN from 4 digits on; if it isn't a match yet, keep typing (5–10 digit PINs) or press Enter.
    if (next.length >= 4) identify(next, { silent: true });
  };
  const backspace = () => setPin((p) => { latestPin.current = p.slice(0, -1); return latestPin.current; });

  const status = identified?.shift?.status; // "in" | "on_break" | undefined (no open shift)

  const trigger =
    variant === "chip" ? (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="staff-clock-chip"
        className="group flex items-center gap-4 rounded-2xl border-2 border-slate-200 bg-white px-5 py-4 text-left transition hover:border-emerald-400 hover:shadow-md active:scale-[0.98]"
      >
        <span className="h-11 w-11 shrink-0 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center transition group-hover:bg-emerald-600 group-hover:text-white">
          <Clock className="h-5 w-5" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block font-display font-semibold text-slate-900">Staff clock-in / out</span>
          <span className="block text-xs text-slate-500">Tap and enter your PIN</span>
        </span>
        <ChevronRight className="h-4 w-4 text-slate-300 transition group-hover:text-emerald-600 group-hover:translate-x-0.5" />
      </button>
    ) : (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="staff-clock-tile"
        className={`group flex flex-col items-start justify-between gap-6 rounded-2xl border-2 border-slate-200 bg-white/70 px-8 py-8 text-slate-900 backdrop-blur-xl transition active:scale-[0.98] hover:border-primary/40 hover:bg-white min-h-[180px] ${wide ? "md:col-span-2" : ""}`}
      >
        <Clock className="h-12 w-12 text-primary" strokeWidth={2} />
        <div className="flex items-end justify-between w-full gap-3">
          <div>
            <div className="font-display text-3xl font-semibold tracking-tight">Staff clock-in</div>
            <div className="text-sm uppercase tracking-widest text-slate-500 mt-1">
              Tap, enter your PIN, clock in or out
            </div>
          </div>
          {wide && (
            <div className="hidden md:flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
              <Coffee className="h-4 w-4" /> Breaks supported
            </div>
          )}
        </div>
      </button>
    );

  const heading = stage === "pin" ? "Employee PIN" : stage === "enroll" ? "Face ID setup" : stage === "actions" ? `Hi ${identified?.name?.split(" ")[0] || ""}` : lastDone?.label || "Done";
  const sub = stage === "pin" ? "Enter your kiosk PIN to clock in or out" : stage === "enroll" ? "First-time setup · takes 5 seconds" : stage === "actions" ? "What would you like to do?" : "Have a great shift!";

  return (
    <>
      {trigger}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`${stage === "pin" ? "sm:max-w-xs" : "sm:max-w-sm"} rounded-3xl p-6 text-center select-none`} data-testid="staff-clock-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
          <DialogHeader className="items-center space-y-0">
            <DialogTitle className="font-display text-lg font-bold text-slate-900">{heading}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">{sub}</DialogDescription>
          </DialogHeader>

          {stage === "pin" && (
            <div>
              <div className="mb-4 flex justify-center gap-2" data-testid="staff-pin-display">
                {[0, 1, 2, 3, 4, 5].map((i) => <span key={i} className={`h-3 w-3 rounded-full transition ${i < pin.length ? "bg-slate-900" : "bg-slate-200"}`} />)}
                {pin.length > 6 && <span className="self-center text-xs text-slate-500">+{pin.length - 6}</span>}
              </div>
              <div className="grid grid-cols-3 gap-2" data-testid="staff-pin-keypad">
                {["1","2","3","4","5","6","7","8","9"].map((d) => (
                  <button key={d} type="button" {...tap(() => pressDigit(d))} disabled={busy} data-testid={`pin-key-${d}`}
                    className="h-14 rounded-xl bg-slate-100 text-lg font-semibold text-slate-900 hover:bg-slate-200 active:scale-95 disabled:opacity-50 transition">{d}</button>
                ))}
                <button type="button" {...tap(backspace)} disabled={busy || !pin} data-testid="pin-key-back"
                  className="h-14 rounded-xl bg-slate-100 text-slate-900 hover:bg-slate-200 disabled:opacity-50 transition inline-flex items-center justify-center">
                  <Delete className="h-5 w-5" />
                </button>
                <button type="button" {...tap(() => pressDigit("0"))} disabled={busy} data-testid="pin-key-0"
                  className="h-14 rounded-xl bg-slate-100 text-lg font-semibold text-slate-900 hover:bg-slate-200 active:scale-95 disabled:opacity-50 transition">0</button>
                <button type="button" {...tap(() => identify(pin))} disabled={busy || pin.length < 4} data-testid="pin-key-enter"
                  className="h-14 rounded-xl bg-primary text-lg font-semibold text-white hover:shadow disabled:opacity-50 transition inline-flex items-center justify-center">
                  {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Go"}
                </button>
              </div>
            </div>
          )}

          {stage === "enroll" && identified && (
            <div className="space-y-4" data-testid="staff-face-enroll">
              <div className="text-center">
                <div className="font-display text-lg font-semibold text-slate-900">Hi {identified.name.split(" ")[0]} — let's set up your Face ID</div>
                <p className="text-sm text-slate-500 mt-1">Look straight at the camera and take your photo. Future punches will check it's really you.</p>
              </div>
              <div className="relative mx-auto w-full max-w-xs aspect-[4/3] rounded-2xl overflow-hidden bg-slate-900 border border-slate-200">
                {enrollPhoto ? (
                  <img src={enrollPhoto} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ transform: "scaleX(-1)" }} data-testid="staff-face-enroll-preview" />
                ) : (
                  <video ref={videoRef} playsInline muted autoPlay data-testid="staff-face-enroll-camera" className="absolute inset-0 h-full w-full object-cover" style={{ transform: "scaleX(-1)" }} />
                )}
                {!cameraReady && !enrollPhoto && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-white/80 text-xs">
                    {cameraDenied ? <><CameraOff className="h-6 w-6" /> Camera unavailable on this device</> : <><Camera className="h-6 w-6 animate-pulse" /> Starting camera…</>}
                  </div>
                )}
              </div>
              {!enrollPhoto ? (
                <button onClick={() => { const p = captureFrame(); if (p) setEnrollPhoto(p); else toast.error("Camera not ready yet"); }} disabled={!cameraReady || busy} data-testid="staff-face-enroll-capture"
                  className="w-full h-14 rounded-xl bg-primary text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 transition">
                  <Camera className="h-5 w-5" /> Take my photo
                </button>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setEnrollPhoto(null)} disabled={busy} data-testid="staff-face-enroll-retake"
                    className="h-14 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-50 transition">
                    <RotateCcw className="h-5 w-5" /> Retake
                  </button>
                  <button onClick={enrollFace} disabled={busy} data-testid="staff-face-enroll-save"
                    className="h-14 rounded-xl bg-emerald-600 text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-60 transition">
                    {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Check className="h-5 w-5" />} Use this photo
                  </button>
                </div>
              )}
              <button onClick={() => setStage("actions")} data-testid="staff-face-enroll-skip"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center gap-2 transition">
                Skip for now — clock in without Face ID
              </button>
            </div>
          )}

          {stage === "actions" && identified && (
            <div className="space-y-3 text-left" data-testid="staff-clock-actions">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-center gap-3">
                {/* Live camera preview — small but visible so staff know a photo is captured. */}
                <div className="relative flex-shrink-0 h-16 w-20 rounded-lg overflow-hidden bg-slate-900 border border-slate-200">
                  <video
                    ref={videoRef}
                    playsInline
                    muted
                    autoPlay
                    data-testid="staff-clock-camera"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ transform: "scaleX(-1)" }}
                  />
                  {!cameraReady && (
                    <div className="absolute inset-0 flex items-center justify-center text-white/80">
                      {cameraDenied ? <CameraOff className="h-5 w-5" /> : <Camera className="h-5 w-5 animate-pulse" />}
                    </div>
                  )}
                  {cameraReady && (
                    <span className="absolute bottom-1 right-1 inline-flex items-center gap-1 rounded bg-rose-500/90 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                      <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> live
                    </span>
                  )}
                </div>
                <div className="min-w-0">
                  <div className="font-display text-lg font-semibold text-slate-900 truncate">{identified.name}</div>
                  <div className="text-xs uppercase tracking-wider text-slate-500 mt-0.5">
                    {identified.role} · {status === "in" ? "Currently working" : status === "on_break" ? "Currently on break" : "Currently clocked out"}
                  </div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 mt-0.5">
                    {identified.face_required
                      ? (cameraDenied ? "Face check ON — camera required, punches will be blocked" : "Face check ON — look at the camera")
                      : (cameraDenied ? "Camera off — admin will see no photo" : "Photo captured on action")}
                  </div>
                </div>
              </div>

              {!status && (
                <button
                  onClick={() => submitClock("in")}
                  disabled={busy}
                  data-testid="clock-action-in"
                  className="w-full h-14 rounded-xl bg-emerald-600 text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-emerald-700 disabled:opacity-60 transition"
                >
                  <LogIn className="h-5 w-5" /> Clock in
                </button>
              )}
              {status === "in" && (
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => submitClock("break_start")}
                    disabled={busy}
                    data-testid="clock-action-break-start"
                    className="h-14 rounded-xl bg-amber-500 text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-amber-600 disabled:opacity-60 transition"
                  >
                    <Coffee className="h-5 w-5" /> Start break
                  </button>
                  <button
                    onClick={() => submitClock("out")}
                    disabled={busy}
                    data-testid="clock-action-out"
                    className="h-14 rounded-xl bg-slate-900 text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-60 transition"
                  >
                    <LogOut className="h-5 w-5" /> Clock out
                  </button>
                </div>
              )}
              {status === "on_break" && (
                <button
                  onClick={() => submitClock("break_end")}
                  disabled={busy}
                  data-testid="clock-action-break-end"
                  className="w-full h-14 rounded-xl bg-blue-600 text-white font-semibold inline-flex items-center justify-center gap-2 hover:bg-blue-700 disabled:opacity-60 transition"
                >
                  <Check className="h-5 w-5" /> End break
                </button>
              )}

              <button
                onClick={close}
                data-testid="staff-clock-cancel"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 inline-flex items-center justify-center gap-2 transition"
              >
                <X className="h-4 w-4" /> Cancel
              </button>
            </div>
          )}

          {stage === "done" && lastDone && (
            <div className="text-center" data-testid="staff-clock-done">
              {lastDone.photo ? (
                <img
                  src={lastDone.photo}
                  alt=""
                  className="mx-auto h-20 w-20 rounded-full object-cover border-4 border-emerald-300 shadow"
                  style={{ transform: "scaleX(-1)" }}
                />
              ) : (
                <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <Check className="h-8 w-8" />
                </div>
              )}
              <div className="mt-4 font-display text-xl font-semibold text-slate-900">{lastDone.name}</div>
              {lastDone.action === "out" && lastDone.shift?.worked_minutes != null && (
                <div className="mt-3 text-sm text-slate-600">
                  Worked this shift: <b>{Math.floor(lastDone.shift.worked_minutes / 60)}h {lastDone.shift.worked_minutes % 60}m</b>
                  {lastDone.shift.break_minutes ? ` (excl. ${lastDone.shift.break_minutes}m break)` : ""}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
